'use client';

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Address, AddressesApi, CreateAddressDto, UpdateAddressDto } from '@/lib/Apis';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { useAuth } from '@/providers/AuthProvider';

interface UseAddressesReturn {
  addresses: Address[];
  defaultAddress: Address | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  addAddress: (payload: CreateAddressDto) => Promise<Address>;
  updateAddress: (id: string, payload: UpdateAddressDto) => Promise<Address>;
  removeAddress: (id: string) => Promise<void>;
  markAsDefault: (id: string) => Promise<Address>;
}

export function useAddresses(): UseAddressesReturn {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const { data: addresses = [], isLoading, error, refetch } = useQuery({
    queryKey: ['addresses'],
    queryFn: async () => {
      const response = await new AddressesApi(AXIOS_CONFIG).getMyAddresses();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isAuthenticated,
  });

  const sortedAddresses = useMemo(() => {
    return [...addresses].sort((a, b) => {
      if (a.isDefault === b.isDefault) {
        const dateA = a.updatedAt || '';
        const dateB = b.updatedAt || '';
        return dateB.toString().localeCompare(dateA.toString());
      }
      return a.isDefault ? -1 : 1;
    });
  }, [addresses]);

  const defaultAddress = useMemo(
    () => sortedAddresses.find((address) => address.isDefault) || null,
    [sortedAddresses]
  );

  const addMutation = useMutation({
    mutationFn: async (payload: CreateAddressDto) => {
      const response = await new AddressesApi(AXIOS_CONFIG).createAddressForUser(payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateAddressDto }) => {
      const response = await new AddressesApi(AXIOS_CONFIG).updateUserAddress(payload, id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      await new AddressesApi(AXIOS_CONFIG).deleteUserAddress(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    },
  });

  const markAsDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await new AddressesApi(AXIOS_CONFIG).updateDefaultAddress(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
    },
  });

  const refresh = async () => {
    await refetch();
  };

  const addAddress = async (payload: CreateAddressDto) => {
    return addMutation.mutateAsync(payload);
  };

  const updateAddress = async (id: string, payload: UpdateAddressDto) => {
    return updateMutation.mutateAsync({ id, payload });
  };

  const removeAddress = async (id: string) => {
    const addressToDelete = addresses.find(a => a.id === id);
    const wasDefault = addressToDelete?.isDefault;
    
    await removeMutation.mutateAsync(id);
    
    if (wasDefault && addresses.length > 1) {
      const remainingAddresses = addresses.filter(a => a.id !== id);
      if (remainingAddresses.length > 0) {
        await markAsDefaultMutation.mutateAsync(remainingAddresses[0].id);
      }
    }
  };

  const markAsDefault = async (id: string) => {
    return markAsDefaultMutation.mutateAsync(id);
  };

  return {
    addresses: sortedAddresses,
    defaultAddress,
    isLoading,
    error: error as Error | null,
    refresh,
    addAddress,
    updateAddress,
    removeAddress,
    markAsDefault,
  };
}
